<template>
  <el-dialog :visible.sync="visibleSync" :append-to-body="true" width="50%" :show-close="true"
    :close-on-click-modal="true" :close-on-press-escape="true" :destroy-on-close="true"
    class="image-detail-modal" top="6vh" @close="closeModal">
    <div class="image-detail-content">
      <div class="image-container">
        <img v-if="image && image.url" :src="image.url" :alt="image.alt || image.description || 'Hình ảnh chi tiết'"
          class="detail-image" @load="imageLoaded = true" @error="imageError = true" />

        <!-- Loading state -->
        <div v-if="!imageLoaded && !imageError" class="loading-state">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Đang tải...</span>
          </div>
          <p class="mt-2 text-muted">Đang tải hình ảnh...</p>
        </div>

        <!-- Error state -->
        <div v-if="imageError" class="error-state">
          <i class="fa fa-exclamation-triangle text-danger" style="font-size: 3rem;"></i>
          <p class="mt-2 text-danger">Không thể tải hình ảnh</p>
        </div>
      </div>

      <!-- Navigation buttons for multiple images -->
      <div v-if="hasMultipleImages" class="image-navigation">
        <button class="btn btn-outline-primary btn-sm mr-2" @click="previousImage" :disabled="!canGoPrevious">
          <i class="fa fa-chevron-left mr-1"></i>
          Hình trước
        </button>
        <span class="image-counter">
          {{ currentImageIndex + 1 }} / {{ totalImages }}
        </span>
        <button class="btn btn-outline-primary btn-sm ml-2" @click="nextImage" :disabled="!canGoNext">
          Hình sau
          <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>

    <div slot="footer" class="dialog-footer d-flex justify-content-end">
      <button class="btn btn-primary font-weight-bold" @click="closeModal">
        Đóng
      </button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'ImageDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    image: {
      type: Object,
      default: () => ({})
    },
    images: {
      type: Array,
      default: () => []
    },
    currentIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      visibleSync: this.visible,
      imageLoaded: false,
      imageError: false,
      currentImageIndex: this.currentIndex
    };
  },
  computed: {
    hasMultipleImages() {
      return this.images && this.images.length > 1;
    },
    totalImages() {
      return this.images ? this.images.length : 0;
    },
    canGoPrevious() {
      return this.currentImageIndex > 0;
    },
    canGoNext() {
      return this.currentImageIndex < this.totalImages - 1;
    }
  },
  watch: {
    visible(v) {
      this.visibleSync = v;
      if (v) {
        this.resetImageState();
        // Scroll lên top khi modal mở
        this.$nextTick(() => {
          this.scrollModalToTop();
        });
      }
    },
    visibleSync(v) {
      this.$emit('update:visible', v);
    },
    image(newImage) {
      if (newImage) {
        this.resetImageState();
      }
    }
  },
  methods: {
    closeModal() {
      this.visibleSync = false;
    },

    resetImageState() {
      this.imageLoaded = false;
      this.imageError = false;

      // Force modal hiển thị đúng ngay từ đầu
      this.$nextTick(() => {
        this.forceModalDisplay();
      });
    },

    previousImage() {
      if (this.canGoPrevious) {
        this.currentImageIndex--;
        this.$emit('image-changed', this.currentImageIndex);
      }
    },

    nextImage() {
      if (this.canGoNext) {
        this.currentImageIndex++;
        this.$emit('image-changed', this.currentImageIndex);
      }
    },

    forceModalDisplay() {
      // Force modal backdrop và dialog hiển thị đúng
      const modalWrapper = document.querySelector('.el-dialog__wrapper');
      const modal = document.querySelector('.el-dialog');
      const vModal = document.querySelector('.v-modal');

      if (modalWrapper) {
        modalWrapper.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        modalWrapper.style.opacity = '1';
        modalWrapper.style.zIndex = '2001';
      }

      if (modal) {
        modal.style.backgroundColor = '#fff';
        modal.style.opacity = '1';
        modal.style.zIndex = '2002';
      }

      if (vModal) {
        vModal.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        vModal.style.opacity = '1';
        vModal.style.zIndex = '2000';
      }

      // Scroll modal lên top để button "Đóng" hiển thị ngay
      this.$nextTick(() => {
        this.scrollModalToTop();
      });
    },

    scrollModalToTop() {
      // Scroll modal content lên top
      const modalContent = this.$el.querySelector('.el-dialog__body');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }

      // Scroll toàn bộ modal lên top
      const modal = this.$el.querySelector('.el-dialog');
      if (modal) {
        modal.scrollTop = 0;
      }

      // Scroll window lên top nếu cần
      window.scrollTo(0, 0);
    }
  }
};
</script>

<style lang="scss" scoped>
.image-detail-modal {
  // Đảm bảo modal hiển thị rõ ràng, không bị mờ
  opacity: 1 !important;
  visibility: visible !important;

  .image-detail-content {
    .image-container {
      text-align: center;
      margin-bottom: 1rem;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;
      border-radius: 8px;
      padding: 1rem;

      .detail-image {
        max-width: 100%;
        max-height: 70vh;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        // Đảm bảo ảnh hiển thị rõ ràng
        opacity: 1 !important;
        visibility: visible !important;
        filter: none !important;
      }

      .loading-state,
      .error-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2rem;
      }
    }

    .image-info {
      background: #f8f9fa;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1rem;

      .info-title {
        color: #495057;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      .info-description {
        margin: 0;
        color: #6c757d;
        line-height: 1.5;
      }
    }

    .image-navigation {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      margin-bottom: 1rem;

      .image-counter {
        font-weight: 600;
        color: #495057;
        min-width: 60px;
        text-align: center;
      }
    }
  }

  .image-actions {
    display: flex;
    gap: 0.5rem;
  }
}

// Responsive design
@media (max-width: 768px) {
  .image-detail-modal {
    .image-detail-content {
      .image-container {
        min-height: 300px;

        .detail-image {
          max-height: 50vh;
        }
      }
    }
  }
}
</style>
